import { msg } from "@omegagrid/localize";
import { ColorTypes, MenuItem } from "@omegagrid/core";
import { FormContainer } from "@omegagrid/form";
import { NodeModel, NodeSource, TreeSource } from "@omegagrid/tree";
import rp, { BrowserAdapter, BrowserOptions } from "@reporting/rp-app-host";
import { Page, File as WikiFile } from '@reporting/rp-types/wiki';
import { FolderFormAdapter, FileFormAdapter, PageFormAdapter } from '../adapters';
import { TreeItem } from "../types";
import { buildWikiDownloadUrl, copyWikiPageLink, getSidebar } from "../helpers";

export class IdeBrowserAdapter extends BrowserAdapter {

	constructor(
		private a: number,
		private ideLang: string
	) { super() }

	async fetchPages(items: TreeItem[]): Promise<TreeItem[]>  {
		const pages = await rp.ms('wiki').get<Page[]>('pages/ide', { lang: this.ideLang, accounting: this.a });
		if (!pages) return items;
		pages.forEach(page => {
			items.push({
				type: 'page',
				id: page.id_wiki_page,
				name: page.name,
				lang: page.lang,
				main_page: page.main_page,
				sidebar: page.sidebar
			});
		});
		return items;		
	}

	async fetchFiles(items: TreeItem[]): Promise<TreeItem[]>  {
		const files = await rp.ms('wiki').get<WikiFile[]>(`files`, { accounting: this.a });
		if (!files) return items;
		files.forEach(file => {
			items.push({
				type: 'file',
				id: file.id_wiki_file,
				name: file.filename,
				lang: file.lang || this.ideLang,
				main_page: false,
				sidebar: false
			});
		});
		return items;
	}
	
	async getTree(): Promise<TreeSource> {
		const pages: TreeItem[] = [];
		await this.fetchPages(pages);
		await this.fetchFiles(pages);
		if (!pages) return null;
		// sort all items by name
		pages.sort((a, b) => {
			const nameA = a.name;
			const nameB = b.name;
			if(nameA < nameB) return -1;
			if(nameA > nameB) return 1;
			return 0;
		});
		const createdFolders = new Set<string>();
		const folderItems: NodeSource[] = [];
		const items: NodeSource[] = [];

		pages.forEach(item => {
			let nodeLvl = 0;
			let itemName = item.name;
			let nameParts = item.name.split('/');
			// if the item is in a folder
			if (nameParts.length > 1) {
				itemName = nameParts.pop();
				nameParts.forEach((folderName, index) => {
					const path = nameParts.slice(0, index + 1).join('/');
					if (!createdFolders.has(path)) {
						createdFolders.add(path);
						folderItems.push({
							k: path,
							v: folderName,
							l: index,
							collapsed: true,
							folder: BrowserAdapter.folderOptions,
							props: {item, menuItems: [
								{ key: 'renameFolder', value: msg('Rename'), icon: 'pen-to-square' },
								{ key: 'newPage', value: msg('New page'), icon: 'file-lines' },
								{ key: 'newFile', value: msg('New file'), icon: 'file' },
							]}
						});
					}
					nodeLvl++;
				});
			}
			// create a new item
			let key: string;
			const menuItems: MenuItem[] = [
				{ key: 'properties', value: msg('Properties'), icon: 'gear' },
				{ key: 'delete', value: msg('Delete'), icon: 'trash', color: ColorTypes.Red }
			];
			if (item.type == 'page') {
				menuItems.unshift(
					{ key: 'toggle', value: msg('Show')+'/'+msg('Hide'), icon: 'eye' },
					{ key: 'copyLink', value: msg('Copy link'), icon: 'link' }
				);
				key = `wiki-${item.lang != this.ideLang ? 'pageForm' : 'pageEditor'}-${this.a}-${this.ideLang}-${item.id}`;
			} else {
				menuItems.unshift(
					{ key: 'copyImgMarkdown', value: msg('Copy Markdown'), icon: 'arrow-up-right-from-square' },
					{ key: 'download', value: msg('Download'), icon: 'download' },
				);
				key = `${itemName}`;
			}

			const newItem: NodeSource = {
				k: key,
				v: itemName,
				l: nodeLvl,
				icon: { icon: `${item.type == 'page' ? 'file-lines' : 'image'}` },
				props: { item, menuItems },
			};
			if (nodeLvl == 0) items.push(newItem);
			else folderItems.push(newItem);
		});
		// sort items by name and main_page flag
		items.sort((a, b) => {
			const itemA = a.props.item as TreeItem;
			const itemB = b.props.item as TreeItem;
			if (itemA.main_page && !itemB.main_page) return -1;
			if (a.v < b.v) return -1;
			if (a.v > b.v) return 1;
			return 0;
		});
		return [...items, ...folderItems];
	}

	getMenuItems(node: NodeModel): MenuItem[] {
		const items: MenuItem[] = [];
		const item = node.properties?.item as TreeItem;
		if (item?.lang && item.lang == this.ideLang) {
			if (node.properties?.menuItems) {
				items.push(...node.properties.menuItems as MenuItem[]);
			}
		}
		return items;
	}

	async onAction(key: string, node: NodeModel) {
		const item = node.properties?.item as TreeItem;
		const nameWithPath = item?.name;
		const name = nameWithPath?.split('/').pop();
		if (node.folder) { // folder actions
			let component: FormContainer;
			switch (key) {
				case 'renameFolder':
					component = await rp.host.createComponent(`wiki-folderForm-${this.a}-${this.ideLang}`) as unknown as FormContainer;
					(component.adapter as FolderFormAdapter).path = node.key;
					rp.host.openModalSidebar({component: component, caption: msg('Rename folder')});
					break;
				case 'newPage':
					component = await rp.host.createComponent(`wiki-pageForm-${this.a}-${this.ideLang}`) as unknown as FormContainer;
					(component.adapter as PageFormAdapter).path = node.key + '/';
					rp.host.openModalSidebar({component: component, caption: msg('Create new page')});
					break;
				case 'newFile':
					component = await rp.host.createComponent(`wiki-fileForm-${this.a}`) as unknown as FormContainer;
					(component.adapter as FileFormAdapter).path = node.key + '/';
					rp.host.openModalSidebar({component: component, caption: msg('Create new file')});
					break;
			}
		} else if (item.type == 'page') { // page actions
			switch (key) {
				case 'toggle':
					await rp.ms('wiki').patch(`pages/${item.id}/toggle`, null, { lang: item.lang, accounting: this.a });
					item.sidebar = !item.sidebar;
					break;
				case 'properties':
					rp.host.openModalSidebar({id: `${node.key.replace('pageEditor', 'pageForm')}`, caption: msg('Page properties')});
					break;
				case 'copyLink':
					await copyWikiPageLink(this.a, nameWithPath);
					break;
				case 'delete':
					if (item) {
						const command = await rp.host.openMenu({
							content: 'yes_no',
							text: `${msg('Are you sure you want to delete')} '${nameWithPath}'?`
						});
						if (command?.id == 'Y') {
							await rp.ms('wiki').delete(`pages/${item.id}`, { lang: item.lang, accounting: this.a });
							getSidebar()?.refresh();
						}
					}
					break;
			}
		} else if (item.type == 'file') { // file actions
			switch (key) {
				case 'download':
					window.open(buildWikiDownloadUrl(this.a, item.id), '_blank');
					break;
				case 'copyImgMarkdown':
					await navigator.clipboard.writeText(`![1.00](${name})`);
					rp.host.bucket.showAlert({ type: 'success', title: 'Wiki', content: msg('Copied to clipboard') });
					break;
				case 'properties':
					rp.host.openModalSidebar({id: `wiki-fileForm-${this.a}-${item.id}`, caption: msg('File properties')});
					break;
				case 'delete':
					if (item) {
						const command = await rp.host.openMenu({
							content: 'yes_no',
							text: `${msg('Are you sure you want to delete')} '${nameWithPath}'?`
						});
						if (command?.id == 'Y') {
							await rp.ms('wiki').delete(`files/${item.id}`, { accounting: this.a });
							this.browser.deleteNode(node);
						}
					}
					break;
			}
		}
	}

	getOptions(): Partial<BrowserOptions> {
		return this.createOptions({
			selectMode: 'none',
			expanders: false,
			search: false,
			contextMenuButton: true,
			afterRenderer: (div, node) => {
				let borderColor = 'transparent';
				const item = node.properties?.item as TreeItem;
				if (node.folder) {
					if (item.lang && item.lang != this.ideLang) borderColor = `var(--og-colortype-${ColorTypes.Red}-border-color)`;
				} else {
					if (item.type == 'page') {
						if (item.lang != this.ideLang) borderColor = `var(--og-colortype-${ColorTypes.Red}-border-color)`;
						else borderColor = `var(--og-colortype-${item.sidebar ? ColorTypes.Green : ColorTypes.Gray}-border-color)`;
					}
				}
				div.style.borderLeft = `3px solid ${borderColor}`;
			},
		});
	}

}
